'use client';

import { getImageDimensions } from '@sanity/asset-utils';
import useEmblaCarousel from 'embla-carousel-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Lightbox,
  type RenderSlideProps,
  type SlideImage,
} from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import { cn } from '../../../lib/utils';
import { SanityImage } from '../../../sanity/utils/components/sanity-image';
import { urlForImage } from '../../../sanity/utils/queries/url-for-image';
import { Container } from '../../container';
import { LightboxImage } from '../../lightbox/lightbox-image';
import type {
  GalleryItem,
  GalleryMediaSlide,
} from '../../lightbox/lightbox-types';
import LightboxVideo from '../../lightbox/lightbox-video';
import { Video } from '../../video';

import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/styles.css';

function ProductDetailsHeroRoot({ children }: { children: ReactNode }) {
  return (
    <Container
      className="relative isolate grid w-full gap-8 py-8 lg:grid-cols-[1fr_500px] lg:items-start xl:gap-12"
      asChild
    >
      <article className="relative isolate min-h-dvh overflow-visible">
        {children}
      </article>
    </Container>
  );
}

function Gallery({ children }: { children: ReactNode }) {
  return <section className="relative isolate">{children}</section>;
}

// LATERDO bring back dynamic after figuring out how to do that without crashing Google schema verification
// LATERDO add lightbox video
// const Lightbox = dynamic(
//   () => import('yet-another-react-lightbox').then((mod) => mod.default),
//   {
//     ssr: false,
//   }
// );

// const LightboxImage = dynamic(
//   () =>
//     import('../../lightbox/lightbox-image').then((mod) => mod.LightboxImage),
//   {
//     ssr: false,
//   }
// );

function getVideoThumbnail(playbackId: string) {
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=0`;
}

function renderSlide(props: RenderSlideProps) {
  const slide = props.slide as GalleryMediaSlide;
  if (slide.type === 'video') {
    return <LightboxVideo {...props} />;
  }
  return <LightboxImage {...props} />;
}

function GalleryDesktop({
  gallery,
  className,
  overlay,
  firstImageOverlay,
  secondImageOverlay,
}: {
  gallery: GalleryItem[];
  className?: string;
  overlay?: ReactNode;
  firstImageOverlay?: ReactNode;
  secondImageOverlay?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const assetCount = gallery?.length;

  const slides = gallery
    .map((media): GalleryMediaSlide | null => {
      if (media.type === 'image') {
        if (!media?.asset) return null;
        const dimensions = getImageDimensions(media.asset);
        const url = urlForImage(media).url();
        return {
          type: 'image',
          src: url,
          alt: media.asset.altText || '',
          width: dimensions.width,
          height: dimensions.height,
        };
      }
      if (media.type === 'video') {
        const thumbnailUrl = getVideoThumbnail(media.playbackId);
        return {
          type: 'video',
          playbackId: media.playbackId,
          src: thumbnailUrl,
          alt: 'Video',
          width: 1920,
          height: 1080,
          aspectRatio: media.aspectRatio || '16:9',
        };
      }
      return null;
    })
    .filter((slide): slide is GalleryMediaSlide => slide !== null);

  if (!gallery || gallery.length === 0) return null;

  return (
    <>
      <section className={cn('relative isolate hidden lg:grid', className)}>
        <main
          className={cn(
            'grid gap-3',
            assetCount === 1 ? 'grid-cols-1' : 'grid-cols-2'
          )}
        >
          {gallery.map((item, i) => {
            if (item.type === 'image') {
              return (
                <figure
                  key={item.asset?._ref}
                  className="rounded-theme-sm ac:ring ac:ring-primary-outline transition-all ease-in-out"
                >
                  <div
                    onClick={(e) => {
                      // Prevent opening lightbox if clicking on wishlist button
                      if (
                        (e.target as HTMLElement).closest(
                          'button[aria-label*="wishlist"], button[aria-label*="Wishlist"]'
                        )
                      ) {
                        return;
                      }
                      setCurrentIndex(i);
                      setOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        // Prevent opening lightbox if clicking on wishlist button
                        if (
                          (e.target as HTMLElement).closest(
                            'button[aria-label*="wishlist"], button[aria-label*="Wishlist"]'
                          )
                        ) {
                          return;
                        }
                        setCurrentIndex(i);
                        setOpen(true);
                      }
                    }}
                    tabIndex={0}
                    // biome-ignore lint/a11y/useSemanticElements: <explanation>
                    role="button"
                    aria-label={`View ${item.type} ${i + 1} of ${gallery.length}`}
                    className="relative isolate aspect-h-1 aspect-w-1 h-full w-full cursor-pointer"
                  >
                    <SanityImage
                      image={item}
                      fill
                      quality={100}
                      sizes="(max-width: 768px) 90vw, 70vw"
                      priority={i === 0}
                      className="h-full w-full cursor-zoom-in select-none bg-inherit object-contain object-center"
                    />
                    {overlay && (
                      <div className="absolute inset-0 z-20">{overlay}</div>
                    )}
                    {firstImageOverlay && i === 0 && (
                      <div className="absolute inset-0 z-20">
                        {firstImageOverlay}
                      </div>
                    )}
                    {secondImageOverlay && i === 1 && assetCount > 1 && (
                      <div className="absolute inset-0 z-20">
                        {secondImageOverlay}
                      </div>
                    )}
                  </div>
                </figure>
              );
            }
            if (item.type === 'video') {
              return (
                <button
                  type="button"
                  onClick={() => {
                    setCurrentIndex(i);
                    setOpen(true);
                  }}
                  aria-label={`View ${item.type} ${i + 1} of ${gallery.length}`}
                  aria-pressed={i === currentIndex}
                  key={item.playbackId}
                  className="relative isolate aspect-h-1 aspect-w-1 h-full w-full"
                >
                  <Video
                    playbackId={item.playbackId}
                    resolution="HD"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    controlled={false}
                    className="object-contain"
                  />
                </button>
              );
            }
          })}
        </main>
      </section>
      <div className="bg-white">
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          slides={slides as SlideImage[]}
          render={{ slide: renderSlide }}
          plugins={[Counter]}
          carousel={{ preload: 3 }}
          index={currentIndex}
          controller={{
            // We want to disable the close on interaction to avoid accidental closing
            // when the user is interacting with other elements
            closeOnBackdropClick: false,
          }}
          toolbar={{
            buttons: ['close'],
          }}
          counter={{
            container: {
              style: {
                filter: 'none',
                top: '20px',
                bottom: 'unset',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#ffffff',
                color: '#000000',
                padding: '6px 16px',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: '500',
              },
            },
          }}
          animation={{
            fade: 500,
            swipe: 300,
          }}
          noScroll={{
            disabled: true,
          }}
          styles={{
            container: {
              backgroundColor: '#ffffff',
            },
            slide: {
              backgroundColor: '#ffffff',
            },
            button: {
              color: '#000000',
              backgroundColor: '#ffffff',
              filter: 'none',
              borderRadius: '9999px',
            },
            navigationNext: {
              boxShadow: 'none !important',
              borderRadius: '9999px',
            },
            navigationPrev: {
              boxShadow: 'none !important',
              borderRadius: '9999px',
            },
            toolbar: {
              backgroundColor: 'transparent',
              // boxShadow: 'none !important',
              left: '20px',
              right: 'unset',
              top: '20px',
            },
          }}
        />
      </div>
    </>
  );
}

function GalleryMobile({
  gallery,
  firstImageOverlay,
}: {
  gallery: GalleryItem[];
  firstImageOverlay?: ReactNode;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
  });

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!gallery || gallery.length === 0) return null;

  return (
    <section className="relative isolate grid lg:hidden">
      <main className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4 pb-12 md:min-h-56 md:gap-8">
          {gallery.map((item, i) => {
            if (item.type === 'image') {
              return (
                <figure
                  key={item.asset?._ref}
                  className="relative max-h-[680px] w-screen max-w-[min(85vw,_calc(680px*var(--image-aspect-ratio)))] flex-[0_0_85vw] overflow-hidden transition-all ease-in-out [--image-aspect-ratio:1] max-lg:max-h-[460px] max-lg:max-w-[min(85vw,_calc(460px*var(--image-aspect-ratio)))] md:aspect-[unset]"
                  style={{ aspectRatio: 1 }}
                >
                  <div className="relative aspect-h-1 aspect-w-1 h-full w-full">
                    <SanityImage
                      image={item}
                      fill
                      priority={i === 0}
                      sizes="(max-width: 768px) 90vw, 70vw"
                      className="h-full w-full cursor-zoom-in select-none bg-inherit object-contain object-center"
                    />
                  </div>
                  {firstImageOverlay && i === 0 && firstImageOverlay}
                </figure>
              );
            }
            if (item.type === 'video') {
              return (
                <div
                  key={item.playbackId}
                  className="relative isolate aspect-h-1 aspect-w-1 h-full w-full"
                >
                  <Video
                    playbackId={item.playbackId}
                    resolution="HD"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    controlled={true}
                    className="object-contain"
                  />
                </div>
              );
            }
          })}
        </div>
      </main>
      <div className="absolute bottom-10 z-20 w-full">
        <div className="relative isolate">
          {/* Scroll progress bar */}
          <button
            type="button"
            className="group -bottom-10 -translate-y-1/2 absolute left-0 flex h-4 w-full cursor-pointer touch-none items-center"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const progress = clickX / rect.width;
              const targetIndex = Math.round(progress * (gallery.length - 1));
              scrollTo(Math.max(0, Math.min(targetIndex, gallery.length - 1)));
            }}
            aria-label="Gallery progress bar"
          >
            <div className="relative h-0.5 w-full bg-gray-200 transition-all group-hover:h-0.5">
              <div
                className="absolute top-1/2 h-0.5 bg-gray-900 transition-all duration-300 ease-out group-hover:h-0.5"
                style={{
                  transform: 'translateY(-50%)',
                  width: `${100 / gallery.length}%`,
                  left: `${(currentIndex / Math.max(gallery.length - 1, 1)) * (100 - 100 / gallery.length)}%`,
                }}
              />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}

function Form({ children }: { children: ReactNode }) {
  return (
    <aside className="sticky top-8 flex max-h-screen flex-col gap-4 self-start overflow-y-auto">
      {children}
    </aside>
  );
}

export const ProductDetailsHero = Object.assign(ProductDetailsHeroRoot, {
  GalleryContainer: Gallery,
  GalleryDesktop,
  GalleryMobile,
  Form,
});
